import {
    DefaultButton,
    Dialog,
    DialogFooter,
    Icon,
    IconButton,
    IContextualMenuProps,
    IModalStyles,
    mergeStyleSets,
    Modal,
    Pivot,
    PivotItem,
    PrimaryButton,
    Spinner,
    SpinnerSize,
    Stack,
    Text,
    TooltipHost,
    useTheme,
} from '@fluentui/react';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {toast} from 'react-toastify';
import MarkdownView from 'react-showdown';
import {RootState} from '../../../store/rootReducer';
import {IStorefrontStyle, IStorefrontTemplate, IStorefrontTemplateGalleryItem} from '../../../models/Storefront';
import {
    useDeleteStorefrontTemplateMutation,
    useGetStorefrontTemplateQuery,
    useGetStorefrontTemplatesQuery,
} from '../../../store/endpoints/StorefrontTemplateEndpoints';
import useCan, {STOREFRONT_TEMPLATES} from '../../../permissions/ability';
import useAssistantAvailable from '../../AdaptiveIntelligence/Assistant/useAssistantAvailable';
import {useGetAssistantUsageQuery} from '../../../store/endpoints/AssistantEndpoints';

// Starting style presets chosen on the details view. Only sent when the user actually picked them;
// omitted (quick select from a card) means the API keeps the template's captured defaults.
export interface IStorefrontTemplatePresets {
    paletteId?: string;
    fontPackId?: string;
}

interface IStorefrontTemplatePickerModalProps {
    isOpen: boolean;
    onDismiss: () => void;
    // undefined = start from a blank storefront.
    onSelectTemplate: (template?: IStorefrontTemplate, presets?: IStorefrontTemplatePresets) => void;
    // The details view's primary action. Defaults to creating a storefront ('Start with this design'); the
    // apply-to-an-existing-storefront flow reuses this whole gallery and only needs the verb to change.
    selectLabel?: string;
    onEditTemplate: (template: IStorefrontTemplate) => void;
    // Present = the caller can run the Adaptive Intelligence create flow. The card is still gated on
    // the user's own assistant availability on top of that.
    onSelectAdaptiveIntelligence?: () => void;
}

type FilterKey = 'all' | 'featured' | 'mine';

// Fluent's Modal wraps children in .ms-Modal-scrollableContent, a plain block scroller. That makes
// the whole modal body (header included) scroll as one and keeps the sticky media column from ever
// engaging, since its own flex parent never overflows. Turn the wrapper into the flex column this
// layout expects so the header stays put and the inner areas own their scrolling.
const modalStyles: Partial<IModalStyles> = {
    scrollableContent: {
        display: 'flex',
        flexDirection: 'column',
        flex: '1 1 auto',
        minHeight: 0,
        overflow: 'hidden',
    },
};

const styles = mergeStyleSets({
    body: {
        display: 'flex',
        flexDirection: 'column',
        width: '90vw',
        maxWidth: '1400px',
        height: '85vh',
        overflow: 'hidden',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px 24px 0 24px',
    },
    pivotRow: {
        padding: '0 24px',
    },
    scrollArea: {
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        padding: '16px 24px 24px 24px',
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
        gap: '20px',
    },
    card: {
        position: 'relative',
        border: '1px solid #e1dfdd',
        borderRadius: '6px',
        overflow: 'hidden',
        backgroundColor: '#ffffff',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        selectors: {
            ':hover': {
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                borderColor: '#c7c7c7',
            },
            ':hover .useOverlay': {
                opacity: 1,
            },
        },
    },
    blankCard: {
        border: '2px dashed #b3b3b3',
        borderRadius: '6px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.5em',
        minHeight: '260px',
        cursor: 'pointer',
        color: '#605e5c',
        backgroundColor: '#faf9f8',
        selectors: {
            ':hover': {
                borderColor: '#0078d4',
                color: '#0078d4',
            },
        },
    },
    // The Adaptive Intelligence card. Solid where the blank card is dashed, so it reads as the
    // offer rather than as another empty starting point; the accent colors come from the theme at
    // render time.
    aiCard: {
        border: '2px solid',
        borderRadius: '6px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        gap: '0.5em',
        padding: '1em',
        minHeight: '260px',
        cursor: 'pointer',
        selectors: {
            ':hover': {
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            },
        },
    },
    aiCardCopy: {
        maxWidth: '260px',
    },
    mediaArea: {
        position: 'relative',
        width: '100%',
        aspectRatio: '4 / 3',
        backgroundColor: '#f3f2f1',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    media: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        display: 'block',
    },
    placeholder: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#a19f9d',
        gap: '8px',
    },
    placeholderInitial: {
        fontSize: '48px',
        fontWeight: 700,
        color: '#c8c6c4',
    },
    chevron: {
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        color: '#ffffff',
        backgroundColor: 'rgba(0,0,0,0.35)',
        borderRadius: '50%',
        selectors: {
            ':hover': {backgroundColor: 'rgba(0,0,0,0.55)'},
            '.ms-Button-icon': {color: '#ffffff'},
        },
    },
    chevronLeft: {left: '8px'},
    chevronRight: {right: '8px'},
    dots: {
        position: 'absolute',
        bottom: '8px',
        left: 0,
        right: 0,
        display: 'flex',
        justifyContent: 'center',
        gap: '6px',
    },
    dot: {
        width: '7px',
        height: '7px',
        borderRadius: '50%',
        backgroundColor: 'rgba(255,255,255,0.55)',
    },
    dotActive: {
        backgroundColor: '#ffffff',
    },
    useOverlay: {
        position: 'absolute',
        top: '8px',
        left: '8px',
        opacity: 0,
        transition: 'opacity 0.15s ease-in-out',
    },
    overflowBtn: {
        position: 'absolute',
        top: '4px',
        right: '4px',
        backgroundColor: 'rgba(255,255,255,0.85)',
        borderRadius: '4px',
    },
    cardBody: {
        padding: '12px 14px 14px 14px',
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
    },
    nameRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '8px',
    },
    name: {
        fontWeight: 600,
        fontSize: '15px',
    },
    badgeRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        flexWrap: 'wrap',
    },
    badge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        fontSize: '12px',
        padding: '2px 8px',
        borderRadius: '10px',
        backgroundColor: '#f3f2f1',
        color: '#605e5c',
    },
    badgeFeatured: {
        backgroundColor: '#eff6fc',
        color: '#0078d4',
    },
    badgeDisabled: {
        backgroundColor: '#fde7e9',
        color: '#a4262c',
    },
    description: {
        display: '-webkit-box',
        WebkitLineClamp: 3,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        color: '#605e5c',
        fontSize: '13px',
    },
    loading: {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    empty: {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#a19f9d',
    },
    hidden: {
        display: 'none',
    },
    headerTitleRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.5em',
        minWidth: 0,
    },
    headerName: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    detailsColumns: {
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'flex-start',
        gap: '1em',
        padding: '1em 24px 24px 24px',
    },
    detailsMediaColumn: {
        flex: '3 1 480px',
        minWidth: 0,
        maxWidth: '100%',
        alignSelf: 'flex-start',
        selectors: {
            // Pinned while the info column scrolls, so picking a palette below always shows its
            // gallery image swap without scrolling back up. Only while the columns sit side by
            // side; when they wrap into a single column the pinned hero would cover the content
            // scrolling beneath it.
            '@media (min-width: 960px)': {
                position: 'sticky',
                top: 0,
            },
        },
    },
    detailsInfoColumn: {
        flex: '2 1 300px',
        minWidth: 0,
        maxWidth: '100%',
    },
    detailsHero: {
        position: 'relative',
        width: '100%',
        aspectRatio: '4 / 3',
        backgroundColor: '#f3f2f1',
        borderRadius: '6px',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    thumbStrip: {
        display: 'flex',
        gap: '0.5em',
        overflowX: 'auto',
        paddingTop: '0.5em',
    },
    thumb: {
        flex: '0 0 auto',
        width: '72px',
        aspectRatio: '4 / 3',
        padding: 0,
        borderRadius: '4px',
        overflow: 'hidden',
        cursor: 'pointer',
        backgroundColor: '#f3f2f1',
        border: '2px solid transparent',
    },
    detailsDescription: {
        color: '#605e5c',
    },
    detailsLongDescription: {
        maxHeight: '38vh',
        overflowY: 'auto',
    },
    presetSectionLabel: {
        fontWeight: 600,
    },
    presetGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gap: '0.5em',
    },
    presetOption: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        width: '100%',
        minWidth: 0,
        padding: '6px',
        border: '2px solid transparent',
        borderRadius: '6px',
        backgroundColor: '#faf9f8',
        textAlign: 'left',
        cursor: 'pointer',
        selectors: {
            ':hover': {backgroundColor: '#f3f2f1'},
        },
    },
    swatchStrip: {
        display: 'flex',
        height: '22px',
        borderRadius: '11px',
        overflow: 'hidden',
        border: '1px solid #e1dfdd',
    },
    swatchChip: {
        flex: '1 1 0',
        minWidth: 0,
    },
    presetName: {
        fontSize: '12px',
        color: '#605e5c',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    fontSample: {
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
        padding: '6px 8px',
        borderRadius: '4px',
        border: '1px solid #e1dfdd',
        backgroundColor: '#ffffff',
        overflow: 'hidden',
    },
    fontSampleHeading: {
        fontSize: '18px',
        lineHeight: '22px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    fontSampleBody: {
        fontSize: '12px',
        lineHeight: '16px',
        color: '#605e5c',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
});

// The five palette slots shown as chips, plus the rest of the slot order used to fill in for any
// slot the palette does not define.
const PALETTE_CHIP_SLOTS = ['background', 'surface', 'text', 'accent', 'menuBar'];
const PALETTE_SPARE_SLOTS = ['heading', 'accentText', 'menuBarText', 'border'];

function paletteChipColors(style: IStorefrontStyle): string[] {
    const colors = (style.parts?.colors ?? {}) as {[slot: string]: string | undefined};
    const spares = PALETTE_SPARE_SLOTS.map((slot) => colors[slot]).filter((c): c is string => !!c);
    let spareIndex = 0;
    return PALETTE_CHIP_SLOTS.map((slot) => {
        const value = colors[slot];
        if (value) return value;
        return spareIndex < spares.length ? spares[spareIndex++] : '#f3f2f1';
    });
}

// "'Playfair Display', Georgia, serif" -> "Playfair Display"
function fontDisplayName(stack?: string): string {
    const first = (stack ?? '').split(',')[0]?.trim() ?? '';
    const cleaned = first.replace(/^["']|["']$/g, '').trim();
    return cleaned.length ? cleaned : 'Default';
}

function sortedStyles(styles?: IStorefrontStyle[]): IStorefrontStyle[] {
    return [...(styles ?? [])].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
}

// The template's captured default is the active style; fall back to the first one.
function defaultStyleId(styles: IStorefrontStyle[]): string | undefined {
    if (!styles.length) return undefined;
    return (styles.find((s) => s.isActive === true) ?? styles[0]).id;
}

function sortedGallery(template: IStorefrontTemplate): IStorefrontTemplateGalleryItem[] {
    return [...(template.gallery ?? [])]
        .filter((g) => !!g.url)
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
}

// Featured / My template / Disabled badges, shared by the card and the details view.
const TemplateBadges: React.FC<{template: IStorefrontTemplate; isOwn: boolean}> = ({template, isOwn}) => (
    <div className={styles.badgeRow}>
        {template.isSystem ? (
            <span className={`${styles.badge} ${styles.badgeFeatured}`}>
                <Icon iconName='Globe'/>
                Featured
            </span>
        ) : isOwn ? (
            <span className={styles.badge}>My template</span>
        ) : null}
        {template.disabled && (
            <span className={`${styles.badge} ${styles.badgeDisabled}`}>Disabled</span>
        )}
    </div>
);

interface ITemplateCardProps {
    template: IStorefrontTemplate;
    isOwn: boolean;
    canManage: boolean;
    // Card click opens the in-modal details view; the hover overlay button selects straight away.
    onOpenDetails: () => void;
    onSelect: () => void;
    onEdit: () => void;
    onDelete: () => void;
}

const TemplateCard: React.FC<ITemplateCardProps> = ({template, isOwn, canManage, onOpenDetails, onSelect, onEdit, onDelete}) => {
    const gallery = useMemo(() => sortedGallery(template), [template]);
    const [index, setIndex] = useState(0);

    const safeIndex = gallery.length ? index % gallery.length : 0;
    const current = gallery[safeIndex];

    const step = (e: {stopPropagation: () => void}, delta: number) => {
        e.stopPropagation();
        if (!gallery.length) return;
        setIndex((prev) => (prev + delta + gallery.length) % gallery.length);
    };

    const menuProps: IContextualMenuProps = {
        items: [
            {
                key: 'edit',
                text: 'Edit',
                iconProps: {iconName: 'Edit'},
                onClick: () => {
                    onEdit();
                },
            },
            {
                key: 'delete',
                text: 'Delete',
                iconProps: {iconName: 'Delete'},
                onClick: () => {
                    onDelete();
                },
            },
        ],
    };

    return (
        <div className={styles.card} onClick={onOpenDetails} role='button'>
            <div className={styles.mediaArea}>
                {current ? (
                    current.mediaType === 'video' ? (
                        <video className={styles.media} src={current.url} autoPlay loop muted playsInline/>
                    ) : (
                        <img className={styles.media} src={current.url} alt={current.altText ?? template.name}/>
                    )
                ) : (
                    <div className={styles.placeholder}>
                        <span className={styles.placeholderInitial}>{template.name?.charAt(0)?.toUpperCase() ?? '?'}</span>
                        <Icon iconName='Photo2'/>
                    </div>
                )}

                {gallery.length > 1 && (
                    <>
                        <IconButton
                            className={`${styles.chevron} ${styles.chevronLeft}`}
                            iconProps={{iconName: 'ChevronLeft'}}
                            aria-label='Previous image'
                            onClick={(e) => step(e, -1)}
                        />
                        <IconButton
                            className={`${styles.chevron} ${styles.chevronRight}`}
                            iconProps={{iconName: 'ChevronRight'}}
                            aria-label='Next image'
                            onClick={(e) => step(e, 1)}
                        />
                        <div className={styles.dots}>
                            {gallery.map((g, i) => (
                                <span
                                    key={g.id ?? i}
                                    className={`${styles.dot} ${i === safeIndex ? styles.dotActive : ''}`}
                                />
                            ))}
                        </div>
                    </>
                )}

                {isOwn && canManage && (
                    <IconButton
                        className={styles.overflowBtn}
                        iconProps={{iconName: 'MoreVertical'}}
                        aria-label='Template actions'
                        menuProps={menuProps}
                        onRenderMenuIcon={() => null}
                        onClick={(e) => e.stopPropagation()}
                    />
                )}

                <div className={`useOverlay ${styles.useOverlay}`}>
                    <PrimaryButton
                        text='Use This Template'
                        onClick={(e) => {
                            e.stopPropagation();
                            onSelect();
                        }}
                    />
                </div>
            </div>
            <div className={styles.cardBody}>
                <div className={styles.nameRow}>
                    <Text className={styles.name}>{template.name}</Text>
                </div>
                <TemplateBadges template={template} isOwn={isOwn}/>
                {template.description && <Text className={styles.description}>{template.description}</Text>}
            </div>
        </div>
    );
};

interface ITemplateDetailsViewProps {
    selectLabel?: string;
    // The list payload item. The full record is refetched so long-form copy is present.
    template: IStorefrontTemplate;
    isOwn: boolean;
    canManage: boolean;
    onSelect: (template: IStorefrontTemplate, presets?: IStorefrontTemplatePresets) => void;
    onEdit: (template: IStorefrontTemplate) => void;
}

// Squarespace-style detail page for a single template: gallery on the left, copy and actions on the right.
const TemplateDetailsView: React.FC<ITemplateDetailsViewProps> = ({template, isOwn, canManage, onSelect, onEdit, selectLabel}) => {
    const theme = useTheme();
    const [index, setIndex] = useState(0);

    const detailsQuery = useGetStorefrontTemplateQuery(template.id ?? '', {skip: !template.id});
    // Fall back to the list item until the full record lands.
    const full = detailsQuery.data ?? template;
    const loadingDetails = detailsQuery.isLoading || detailsQuery.isFetching;

    // Style presets bundled with the template. Only present on the full record.
    const palettes = useMemo(() => sortedStyles(full.palettes), [full.palettes]);
    const fontPacks = useMemo(() => sortedStyles(full.fontPacks), [full.fontPacks]);
    const [paletteId, setPaletteId] = useState<string | undefined>();
    const [fontPackId, setFontPackId] = useState<string | undefined>();

    // Defaults land once the full record resolves.
    useEffect(() => setPaletteId(defaultStyleId(palettes)), [palettes]);
    useEffect(() => setFontPackId(defaultStyleId(fontPacks)), [fontPacks]);

    // The list payload has no palettes, so until the full record identifies the captured default
    // the gallery would show every image and then prune the non-matching ones - a visible flicker.
    // Bridge the gap by guessing from the gallery itself: sort order puts the default palette's
    // shots first, so the first tagged image's palette almost always matches the real default and
    // the strip renders its final set from the first paint.
    const provisionalPaletteId = useMemo(() => sortedGallery(full).find((g) => g.paletteId)?.paletteId, [full]);
    const effectivePaletteId = paletteId ?? provisionalPaletteId;

    // The palettes and font packs arrive with the FULL record, a beat after this view paints, and until they
    // do there is nothing where they will be — so the obvious move is to click the primary action without
    // knowing a choice was still loading, and take whatever the template captured by default.
    //
    // Whether this template HAS presets is knowable before they land: the gallery images are tagged with the
    // palette they were shot under, which is what provisionalPaletteId above relies on. Using the same signal
    // means a template with no presets shows no placeholder for presets it was never going to have.
    const expectsPresets = useMemo(() => sortedGallery(full).some((g) => g.paletteId), [full]);
    const presetsPending = loadingDetails && !detailsQuery.data && expectsPresets;

    // The gallery follows the palette choice: untagged images always show; palette-tagged images
    // only while their palette is selected. If filtering would empty the gallery (e.g. every image
    // is tagged for some other palette), fall back to the full set rather than a blank hero.
    const gallery = useMemo(() => {
        const all = sortedGallery(full);
        const filtered = all.filter((g) => !g.paletteId || g.paletteId === effectivePaletteId);
        return filtered.length ? filtered : all;
    }, [full, effectivePaletteId]);
    useEffect(() => setIndex(0), [effectivePaletteId]);
    const safeIndex = gallery.length ? index % gallery.length : 0;
    const current = gallery[safeIndex];

    const step = (delta: number) => {
        if (!gallery.length) return;
        setIndex((prev) => (prev + delta + gallery.length) % gallery.length);
    };

    const renderMedia = (item: IStorefrontTemplateGalleryItem) =>
        item.mediaType === 'video' ? (
            <video className={styles.media} src={item.url} autoPlay loop muted playsInline/>
        ) : (
            <img className={styles.media} src={item.url} alt={item.altText ?? full.name}/>
        );

    return (
        <div className={styles.detailsColumns}>
            <div className={styles.detailsMediaColumn}>
                <div className={styles.detailsHero}>
                    {current ? renderMedia(current) : (
                        <div className={styles.placeholder}>
                            <span className={styles.placeholderInitial}>{full.name?.charAt(0)?.toUpperCase() ?? '?'}</span>
                            <Icon iconName='Photo2'/>
                        </div>
                    )}
                    {gallery.length > 1 && (
                        <>
                            <IconButton
                                className={`${styles.chevron} ${styles.chevronLeft}`}
                                iconProps={{iconName: 'ChevronLeft'}}
                                aria-label='Previous image'
                                onClick={() => step(-1)}
                            />
                            <IconButton
                                className={`${styles.chevron} ${styles.chevronRight}`}
                                iconProps={{iconName: 'ChevronRight'}}
                                aria-label='Next image'
                                onClick={() => step(1)}
                            />
                        </>
                    )}
                </div>
                {gallery.length > 1 && (
                    <div className={styles.thumbStrip}>
                        {gallery.map((g, i) => (
                            <button
                                key={g.id ?? i}
                                type='button'
                                className={styles.thumb}
                                style={i === safeIndex ? {borderColor: theme.palette.themePrimary} : undefined}
                                aria-label={`Show image ${i + 1}`}
                                onClick={() => setIndex(i)}
                            >
                                {renderMedia(g)}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            <div className={styles.detailsInfoColumn}>
                <Stack tokens={{childrenGap: '1em'}}>
                    <TemplateBadges template={full} isOwn={isOwn}/>

                    {full.description && <Text className={styles.detailsDescription}>{full.description}</Text>}

                    {loadingDetails && !detailsQuery.data && <Spinner size={SpinnerSize.small} label='Loading details...' labelPosition='right'/>}

                    {full.longDescription && (
                        <div className={styles.detailsLongDescription}>
                            <MarkdownView markdown={full.longDescription} options={{tables: true, emoji: true}}/>
                        </div>
                    )}

                    {presetsPending && (
                        <Stack tokens={{childrenGap: '0.5em'}}>
                            <Text variant='medium' className={styles.presetSectionLabel}>Color palettes</Text>
                            <Spinner
                                size={SpinnerSize.small}
                                label='Loading the palettes and font packs you can choose from...'
                                labelPosition='right'
                                styles={{root: {justifyContent: 'flex-start'}}}
                            />
                        </Stack>
                    )}

                    {palettes.length > 0 && (
                        <Stack tokens={{childrenGap: '0.5em'}}>
                            <Text variant='medium' className={styles.presetSectionLabel}>Color palettes</Text>
                            <div className={styles.presetGrid}>
                                {palettes.map((p) => (
                                    <button
                                        key={p.id}
                                        type='button'
                                        className={styles.presetOption}
                                        style={p.id === paletteId ? {borderColor: theme.palette.themePrimary} : undefined}
                                        aria-pressed={p.id === paletteId}
                                        onClick={() => setPaletteId(p.id)}
                                    >
                                        <span className={styles.swatchStrip}>
                                            {paletteChipColors(p).map((color, i) => (
                                                <span key={i} className={styles.swatchChip} style={{backgroundColor: color}}/>
                                            ))}
                                        </span>
                                        <span className={styles.presetName}>{p.name}</span>
                                    </button>
                                ))}
                            </div>
                        </Stack>
                    )}

                    {fontPacks.length > 0 && (
                        <Stack tokens={{childrenGap: '0.5em'}}>
                            <Text variant='medium' className={styles.presetSectionLabel}>Font packs</Text>
                            <div className={styles.presetGrid}>
                                {fontPacks.map((f) => {
                                    const fonts = (f.parts?.fonts ?? {}) as {heading?: string; body?: string};
                                    return (
                                        <button
                                            key={f.id}
                                            type='button'
                                            className={styles.presetOption}
                                            style={f.id === fontPackId ? {borderColor: theme.palette.themePrimary} : undefined}
                                            aria-pressed={f.id === fontPackId}
                                            onClick={() => setFontPackId(f.id)}
                                        >
                                            <span className={styles.fontSample}>
                                                <span className={styles.fontSampleHeading} style={{fontFamily: fonts.heading}}>
                                                    Aa {fontDisplayName(fonts.heading)}
                                                </span>
                                                <span className={styles.fontSampleBody} style={{fontFamily: fonts.body}}>
                                                    The quick brown fox jumps over the lazy dog.
                                                </span>
                                            </span>
                                            <span className={styles.presetName}>{f.name}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </Stack>
                    )}

                    <Stack horizontal tokens={{childrenGap: '0.5em'}} wrap>
                        {/* Held back only while presets this template really has are still loading, and only
                            then: on a failed load `loadingDetails` goes false with no data, so the action
                            re-enables rather than stranding the customer on a dead screen. */}
                        <PrimaryButton
                            text={presetsPending ? 'Loading options...' : (selectLabel ?? 'Start with this design')}
                            disabled={presetsPending}
                            onClick={() => onSelect(full, {paletteId, fontPackId})}
                        />
                        {isOwn && canManage && (
                            <DefaultButton text='Edit' iconProps={{iconName: 'Edit'}} onClick={() => onEdit(full)}/>
                        )}
                    </Stack>
                </Stack>
            </div>
        </div>
    );
};

const StorefrontTemplatePickerModal: React.FC<IStorefrontTemplatePickerModalProps> = (props) => {
    const currentOrg = useSelector((state: RootState) => state.settings.selectedOrganization);
    const canManage = useCan(STOREFRONT_TEMPLATES);
    const theme = useTheme();
    const assistantAvailable = useAssistantAvailable();
    const showAiCard = assistantAvailable && !!props.onSelectAdaptiveIntelligence;
    // The rolling-window usage under the card. Only fetched while the picker is open and the card
    // shows; the card disables itself when the window is spent rather than letting the form 429.
    const usageQuery = useGetAssistantUsageQuery(undefined, {skip: !props.isOpen || !showAiCard});
    const generationUsage = usageQuery.data;
    const generationsExhausted = !!generationUsage
        && generationUsage.generationLimit > 0
        && generationUsage.generationUsed >= generationUsage.generationLimit;

    const templatesQuery = useGetStorefrontTemplatesQuery(undefined, {skip: !props.isOpen});
    const [deleteTemplate] = useDeleteStorefrontTemplateMutation();

    const [filter, setFilter] = useState<FilterKey>('all');
    const [pendingDelete, setPendingDelete] = useState<IStorefrontTemplate | undefined>();
    const [deleting, setDeleting] = useState(false);
    // Set = the in-modal details view for that template replaces the grid.
    const [detailsTemplate, setDetailsTemplate] = useState<IStorefrontTemplate | undefined>();
    // The grid stays mounted behind the details view so the filter survives; its scroll offset is
    // restored by hand because a hidden element does not reliably keep one.
    const scrollAreaRef = useRef<HTMLDivElement>(null);
    const scrollOffset = useRef(0);

    useEffect(() => {
        if (!props.isOpen) setDetailsTemplate(undefined);
    }, [props.isOpen]);

    useEffect(() => {
        if (!detailsTemplate && scrollAreaRef.current) {
            scrollAreaRef.current.scrollTop = scrollOffset.current;
        }
    }, [detailsTemplate]);

    const allTemplates = templatesQuery.data?.items ?? [];

    const isOwn = React.useCallback(
        (t: IStorefrontTemplate) => !t.isSystem && !!currentOrg && t.organization === currentOrg,
        [currentOrg]
    );

    const visibleTemplates = useMemo(() => {
        switch (filter) {
            case 'featured':
                return allTemplates.filter((t) => t.isSystem);
            case 'mine':
                // Own templates, including disabled ones (tagged as such).
                return allTemplates.filter((t) => isOwn(t));
            case 'all':
            default:
                // System templates + own templates, but hide own disabled ones here.
                return allTemplates.filter((t) => t.isSystem || (isOwn(t) && !t.disabled));
        }
    }, [allTemplates, filter, isOwn]);

    const confirmDelete = async () => {
        if (!pendingDelete?.id) return;
        setDeleting(true);
        try {
            const res = await deleteTemplate(pendingDelete.id).unwrap();
            if (res.success) {
                toast.success('Template deleted');
            } else {
                toast.error(res.message ?? 'Failed to delete template');
            }
        } catch {
            toast.error('Failed to delete template');
        } finally {
            setDeleting(false);
            setPendingDelete(undefined);
        }
    };

    const loading = templatesQuery.isLoading || templatesQuery.isFetching;

    return (
        <>
            <Modal
                isOpen={props.isOpen}
                onDismiss={props.onDismiss}
                isBlocking={false}
                containerClassName={styles.body}
                styles={modalStyles}
            >
                <div className={styles.header}>
                    <div className={styles.headerTitleRow}>
                        {detailsTemplate && (
                            <IconButton
                                iconProps={{iconName: 'Back'}}
                                aria-label='Back to templates'
                                onClick={() => setDetailsTemplate(undefined)}
                            />
                        )}
                        <Text variant='xLarge' className={styles.headerName}>
                            {detailsTemplate ? detailsTemplate.name : 'Choose a Template'}
                        </Text>
                    </div>
                    <IconButton
                        iconProps={{iconName: 'Cancel'}}
                        aria-label='Close'
                        onClick={props.onDismiss}
                    />
                </div>
                <div className={`${styles.pivotRow} ${detailsTemplate ? styles.hidden : ''}`}>
                    <Pivot
                        selectedKey={filter}
                        onLinkClick={(item) => setFilter((item?.props.itemKey as FilterKey) ?? 'all')}
                    >
                        <PivotItem headerText='All' itemKey='all'/>
                        <PivotItem headerText='Featured' itemKey='featured'/>
                        <PivotItem headerText='My Templates' itemKey='mine'/>
                    </Pivot>
                </div>

                {detailsTemplate && (
                    <TemplateDetailsView
                        key={detailsTemplate.id}
                        template={detailsTemplate}
                        isOwn={isOwn(detailsTemplate)}
                        canManage={canManage}
                        onSelect={props.onSelectTemplate}
                        selectLabel={props.selectLabel}
                        onEdit={props.onEditTemplate}
                    />
                )}

                {loading ? (
                    <div className={`${styles.loading} ${detailsTemplate ? styles.hidden : ''}`}>
                        <Spinner size={SpinnerSize.large} label='Loading templates...'/>
                    </div>
                ) : (
                    <div
                        className={`${styles.scrollArea} ${detailsTemplate ? styles.hidden : ''}`}
                        ref={scrollAreaRef}
                        onScroll={(e) => {
                            scrollOffset.current = e.currentTarget.scrollTop;
                        }}
                    >
                        <div className={styles.grid}>
                            {/* The fastest route to a finished storefront leads the gallery. */}
                            {showAiCard && (
                                <div
                                    className={styles.aiCard}
                                    role='button'
                                    aria-disabled={generationsExhausted}
                                    style={{
                                        borderColor: theme.palette.themePrimary,
                                        backgroundColor: theme.palette.themeLighterAlt,
                                        ...(generationsExhausted ? {cursor: 'default', opacity: 0.6} : {}),
                                    }}
                                    onClick={generationsExhausted ? undefined : props.onSelectAdaptiveIntelligence}
                                >
                                    <Icon
                                        iconName='Robot'
                                        styles={{root: {fontSize: 40, lineHeight: '40px', color: theme.palette.themePrimary}}}
                                    />
                                    <Stack horizontal verticalAlign='center' tokens={{childrenGap: '0.35em'}}>
                                        <Text variant='mediumPlus' styles={{root: {fontWeight: 600}}}>
                                            Create from your website
                                        </Text>
                                        <Text variant='small' styles={{root: {color: theme.palette.neutralSecondary}}}>
                                            (Preview)
                                        </Text>
                                        <TooltipHost content='Adaptive Intelligence is in preview. Usage limits are higher while the preview is running and may change.'>
                                            <Icon
                                                iconName='Info'
                                                styles={{root: {fontSize: 12, color: theme.palette.neutralSecondary, cursor: 'default'}}}
                                            />
                                        </TooltipHost>
                                    </Stack>
                                    <Text variant='small' className={styles.aiCardCopy}>
                                        Adaptive Intelligence reads your website and builds a matching
                                        storefront in minutes. Nothing goes live until you approve it.
                                    </Text>
                                    {generationUsage && (
                                        <Text variant='small' styles={{root: {color: theme.palette.neutralSecondary}}}>
                                            {generationsExhausted
                                                ? `You've used all ${generationUsage.generationLimit} storefront generations ` +
                                                  `for the last ${generationUsage.generationWindowDays} days.` +
                                                  (generationUsage.generationResetsAt
                                                      ? ` More open up ${new Date(generationUsage.generationResetsAt).toLocaleDateString()}.`
                                                      : '')
                                                : `${generationUsage.generationUsed} of ${generationUsage.generationLimit} used ` +
                                                  `in the last ${generationUsage.generationWindowDays} days`}
                                        </Text>
                                    )}
                                </div>
                            )}

                            {/* Start Blank always leads the templates themselves. */}
                            <div
                                className={styles.blankCard}
                                role='button'
                                onClick={() => props.onSelectTemplate(undefined)}
                            >
                                <Icon iconName='Add' styles={{root: {fontSize: 40, lineHeight: '40px'}}}/>
                                <Text variant='mediumPlus' styles={{root: {fontWeight: 600}}}>Start Blank</Text>
                                <Text variant='small'>Start from scratch</Text>
                            </div>

                            {visibleTemplates.map((t) => (
                                <TemplateCard
                                    key={t.id}
                                    template={t}
                                    isOwn={isOwn(t)}
                                    canManage={canManage}
                                    onOpenDetails={() => setDetailsTemplate(t)}
                                    onSelect={() => props.onSelectTemplate(t)}
                                    onEdit={() => props.onEditTemplate(t)}
                                    onDelete={() => setPendingDelete(t)}
                                />
                            ))}
                        </div>
                        {visibleTemplates.length === 0 && (
                            <div className={styles.empty}>
                                <Text>No templates in this view yet.</Text>
                            </div>
                        )}
                    </div>
                )}
            </Modal>

            <Dialog
                hidden={pendingDelete === undefined}
                onDismiss={() => {
                    if (!deleting) setPendingDelete(undefined);
                }}
                dialogContentProps={{
                    title: 'Delete Template',
                    subText: `Are you sure you want to delete "${pendingDelete?.name}"? This action cannot be undone.`,
                }}
                modalProps={{isBlocking: true}}
            >
                <DialogFooter>
                    <PrimaryButton text={deleting ? 'Deleting...' : 'Delete'} disabled={deleting} onClick={confirmDelete}/>
                    <DefaultButton text='Cancel' disabled={deleting} onClick={() => setPendingDelete(undefined)}/>
                </DialogFooter>
            </Dialog>
        </>
    );
};

export default StorefrontTemplatePickerModal;
